import axios from "axios";
import { createFFmpeg, fetchFile } from "@ffmpeg/ffmpeg";

const API_BASE_URL = "https://yyy-back.99ssc.app";

//const API_BASE_URL = "/webhook";
const LOCAL_TEST_API_URL = "http://localhost:3001";
class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 1500000, // 25 minutes to match backend
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.localTestClient = axios.create({
      baseURL: LOCAL_TEST_API_URL,
      timeout: 900000,
    });

    // Initialize FFmpeg instance
    this.ffmpeg = null;
    this.ffmpegLoaded = false;
    this.initializeFFmpeg();

    this.client.interceptors.request.use(
      (config) => {
        // 1. อ่าน Token จาก localStorage
        const token = localStorage.getItem("authToken");

        // 2. ถ้ามี Token, ให้แนบเข้าไปใน Authorization Header
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }

        return config;
      },
      (error) => {
        // จัดการ Error ที่อาจเกิดขึ้นก่อนส่ง Request
        return Promise.reject(error);
      }
    );
  }

  async initializeFFmpeg() {
    try {
      // Check if SharedArrayBuffer is available
      if (typeof SharedArrayBuffer === 'undefined') {
        console.warn("SharedArrayBuffer is not available. FFmpeg functionality will be disabled.");
        console.warn("To enable FFmpeg, the server must serve with Cross-Origin-Embedder-Policy and Cross-Origin-Opener-Policy headers.");
        this.ffmpegLoaded = false;
        return;
      }

      // Use relative path to load FFmpeg from public directory
      this.ffmpeg = createFFmpeg({
        log: true,
        corePath: "/ffmpeg/ffmpeg-core.js",
      });
      
      console.log("Loading FFmpeg...");
      await this.ffmpeg.load();
      console.log("FFmpeg loaded successfully in API service.");
      this.ffmpegLoaded = true;
    } catch (error) {
      console.error("Failed to load FFmpeg in API service:", error);
      if (error.message && error.message.includes('SharedArrayBuffer')) {
        console.error("SharedArrayBuffer is required for FFmpeg. Please ensure your server is configured with the correct headers.");
      }
      this.ffmpegLoaded = false;
    }
  }

  isFFmpegReady() {
    return this.ffmpegLoaded && this.ffmpeg && this.ffmpeg.isLoaded();
  }

  async ensureFFmpegReady() {
    if (!this.isFFmpegReady()) {
      console.log("FFmpeg not ready, attempting to initialize...");
      await this.initializeFFmpeg();
      
      if (!this.isFFmpegReady()) {
        throw new Error("FFmpeg is not available. This may be due to browser security restrictions. The application will use standard file chunking instead.");
      }
    }
    return true;
  }

  async login(email, password) {
    try {
      // **หมายเหตุ:** Endpoint นี้คือสิ่งที่คุณต้องสร้างใน Backend (เช่น /login หรือ /auth/token)
      // มันควรจะรอรับ POST request ที่มี email และ password
      const response = await this.client.post("/auth/login", {
        email,
        password,
      });

      // สมมติว่า API ตอบกลับมาเป็น { success: true, token: "your_jwt_token" }
      if (response.data && response.data.access_token) {
        const token = response.data.access_token;
        const user = response.data.user;
        // 1. บันทึก Token ลงใน localStorage
        localStorage.setItem("authToken", token);
        sessionStorage.setItem("user", JSON.stringify(user));
        // 2. ตั้งค่า Header เริ่มต้นสำหรับ Request ในอนาคต (เผื่อไว้)
        this.client.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${token}`;
        return { success: true, user: user };
      } else {
        throw new Error("API did not return a valid token.");
      }
    } catch (error) {
      console.error("Login failed:", error);
      // คืนค่า error ที่ชัดเจน
      return {
        success: false,
        error: error.response?.data?.message || "อีเมลหรือรหัสผ่านไม่ถูกต้อง",
        error2: error.response?.data?.error || "บางอย่าง ผิดปกติ",
      };
    }
  }

  logout() {
    // 1. ลบ Token ออกจาก localStorage
    localStorage.removeItem("authToken");
    sessionStorage.removeItem("isAuthenticated");
    // 2. ลบ Header เริ่มต้นออก
    delete this.client.defaults.headers.common["Authorization"];
    window.location.reload(); // Optional: reload the page to reset state
    console.log("User logged out and token removed.");
  }

  async uploadChunk(formData) {
    return this.client.post("/upload/chunk", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  async completeChunkedUpload(data) {
    return this.client.post("/upload/complete", data);
  }

  async startChunkedTranscription(
    file,
    onProgress,
    skipTranscription = false,
    duration = 0,
  ) {
    // Calculate chunks based on 10-minute segments instead of file size
    const CHUNK_DURATION_MINUTES = 10;
    const CHUNK_DURATION_SECONDS = CHUNK_DURATION_MINUTES * 60;
    const OVERLAP_SECONDS = 10; // 10-second overlap between chunks
    
    // If duration is available, calculate chunks based on time
    // Otherwise, fall back to file size chunks (for non-media files or when duration is unknown)
    let totalChunks;
    let chunkSizes = [];
    
    if (duration > 0) {
      // Calculate number of chunks based on duration
      totalChunks = Math.ceil(duration / CHUNK_DURATION_SECONDS);
      
      // Calculate approximate file size per chunk based on duration ratio with overlap
      for (let i = 0; i < totalChunks; i++) {
        // Calculate actual start and end times with overlap
        const chunkStartTime = i === 0 ? 0 : (i * CHUNK_DURATION_SECONDS) - OVERLAP_SECONDS;
        const chunkEndTime = Math.min((i + 1) * CHUNK_DURATION_SECONDS, duration);
        
        // Calculate the actual duration of this chunk (including overlap)
        const actualChunkDuration = chunkEndTime - chunkStartTime;
        const chunkDurationRatio = actualChunkDuration / duration;
        const chunkSize = Math.floor(file.size * chunkDurationRatio);
        chunkSizes.push(chunkSize);
      }
    } else {
      // Fallback to 10MB chunks if duration is not available
      const FALLBACK_CHUNK_SIZE = 10 * 1024 * 1024;
      totalChunks = Math.ceil(file.size / FALLBACK_CHUNK_SIZE);
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * FALLBACK_CHUNK_SIZE;
        const end = Math.min(start + FALLBACK_CHUNK_SIZE, file.size);
        chunkSizes.push(end - start);
      }
    }

    const uploadId = `${file.name}-${Date.now()}`;
    console.log(`Starting chunked upload for ${file.name} with ${totalChunks} chunks (${CHUNK_DURATION_MINUTES}-minute segments)`);

    let currentPosition = 0;
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const chunkSize = chunkSizes[chunkIndex];
      const start = currentPosition;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      currentPosition = end;

      const formData = new FormData();
      formData.append("file", chunk);
      formData.append("uploadId", uploadId);
      formData.append("chunkIndex", chunkIndex);
      formData.append("totalChunks", totalChunks);
      formData.append("fileName", file.name);

      try {
        await this.uploadChunk(formData);
        const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        if (onProgress) {
          onProgress(progress);
        }
      } catch (error) {
        console.error(`Error uploading chunk ${chunkIndex}:`, error);
        return {
          success: false,
          error: `Failed to upload chunk ${chunkIndex}.`,
        };
      }
    }

    try {
      const response = await this.completeChunkedUpload({
        uploadId,
        fileName: file.name,
        totalChunks,
        mimetype: file.type,
        skipTranscription,
        duration,
      });

      if (response.data && response.data.googleFileID) {
        return {
          success: true,
          jobId: response.data.googleFileID,
          dbId: response.data.id,
        };
      } else {
        throw new Error(
          "API did not return a valid jobId after completing upload."
        );
      }
    } catch (error) {
      console.error("Error completing transcription process:", error);
      return { success: false, error: error.message };
    }
  }

  async startTranscription(
    file,
    skipTranscription = false,
    duration = 0,
  ) {
    const formData = new FormData();
    formData.append("file", file);
    if (skipTranscription) {
      formData.append("skipTranscription", "true");
    }
    if (duration) {
      formData.append("duration", duration);
    }

    console.log("Starting to call api post...");
    try {
      // NOTE: This webhook path must be different from the old one.
      // Example: /start-transcribe
      const response = await this.client.post(
        "/upload/uploadAndTranscribe",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          // Use a longer timeout just for this initial upload
          timeout: 900000,
        }
      );
      console.log("response.data", response.data);

      // EXPECTING n8n to return: { "jobId": "some-unique-id-from-n8n" }
      if (response.data && response.data.googleFileID) {
        return {
          success: true,
          jobId: response.data.googleFileID,
          dbId: response.data.id,
        };
      } else {
        throw new Error("API did not return a valid jobId.");
      }
    } catch (error) {
      console.error("Error starting transcription process:", error);
      return { success: false, error: error.message };
    }
  }

  async uploadFFmpegChunks(
    file,
    onProgress,
    skipTranscription = false,
    duration = 0,
  ) {
    try {
      // Ensure FFmpeg is ready
      await this.ensureFFmpegReady();
      const CHUNK_DURATION_SECONDS = 1200; // 20 minutes
      const OVERLAP_SECONDS = 10;
      const uploadedFiles = [];

      // Get file extension
      const ext = file.name.split(".").pop().toLowerCase();
      const filename = "input." + ext;
      
      console.log("Starting FFmpeg chunking for:", filename);
      
      // Report initial progress
      if (onProgress) {
        onProgress(5, "Reading file info...");
      }

      // Write file to FFmpeg filesystem
      this.ffmpeg.FS("writeFile", filename, await fetchFile(file));

      // Get total duration
      let totalDuration = 0;
      const logMessages = [];
      this.ffmpeg.setLogger(({ message }) => {
        logMessages.push(message);
      });

      await this.ffmpeg.run("-i", filename);
      
      const match = logMessages
        .find((msg) => msg.includes("Duration:"))
        ?.match(/Duration: (\d+):(\d+):(\d+\.\d+)/);

      if (match) {
        const [_, h, m, s] = match;
        totalDuration = parseInt(h) * 3600 + parseInt(m) * 60 + parseFloat(s);
        console.log("Total duration:", totalDuration);
      }

      if (totalDuration === 0) {
        throw new Error("Could not determine video/audio duration from file metadata.");
      }

      const numChunks = Math.max(1, Math.ceil(totalDuration / CHUNK_DURATION_SECONDS));
      console.log(`Total number of chunks: ${numChunks}`);

      // Process each chunk
      for (let i = 0; i < numChunks; i++) {
        const startTime = Math.max(0, i * CHUNK_DURATION_SECONDS - (i > 0 ? OVERLAP_SECONDS : 0));
        const duration = i === numChunks - 1 
          ? totalDuration - startTime 
          : CHUNK_DURATION_SECONDS + (i > 0 ? OVERLAP_SECONDS : 0);
        
        const outputFileName = `c-${i}-${file.name}`;
        const tempOutputFileName = `c-${i}.${ext}`;

        // Report splitting progress
        if (onProgress) {
          onProgress(
            10 + (i / numChunks) * 40,
            `Splitting file... (${i + 1}/${numChunks})`
          );
        }

        // Split the file using FFmpeg
        await this.ffmpeg.run(
          "-i",
          filename,
          "-ss",
          startTime.toString(),
          "-t",
          duration.toString(),
          "-c",
          "copy",
          tempOutputFileName
        );

        // Read the chunk data
        const data = this.ffmpeg.FS("readFile", tempOutputFileName);
        const chunkBlob = new Blob([data.buffer], { type: file.type });

        // Upload the chunk
        const formData = new FormData();
        formData.append("file", chunkBlob, outputFileName);

        // Report upload progress
        if (onProgress) {
          onProgress(
            50 + ((i + 1) / numChunks) * 45,
            `Uploading chunk... (${i + 1}/${numChunks})`
          );
        }

        const response = await this.uploadChunk(formData);
        if (!response || !response.data) {
          throw new Error(`Failed to upload chunk ${i + 1}`);
        }

        uploadedFiles.push(response.data);

        // Clean up temporary file
        this.ffmpeg.FS("unlink", tempOutputFileName);
      }

      // Clean up input file
      this.ffmpeg.FS("unlink", filename);

      // Report finalizing progress
      if (onProgress) {
        onProgress(98, "Finalizing...");
      }

      // Complete the chunked upload - matching your working component's approach
      const transcribePayload = {
        files: uploadedFiles,
      };

      // Use the backend complete chunked upload endpoint
      const response = await this.completeChunkedUpload({
        uploadId: `ffmpeg-${file.name}-${Date.now()}`,
        fileName: file.name,
        totalChunks: uploadedFiles.length,
        mimetype: file.type,
        skipTranscription,
        duration: totalDuration,
        files: uploadedFiles,
      });
      
      const transcribeResponse = { ok: response.data && response.data.googleFileID };

      if (transcribeResponse.ok) {
        if (onProgress) {
          onProgress(100, "Done!");
        }
        return {
          success: true,
          jobId: "processing", // Will be updated by backend
          dbId: "processing", // Will be updated by backend
        };
      } else {
        throw new Error("Failed to start transcription job.");
      }

    } catch (error) {
      console.error("Error in FFmpeg chunking:", error);
      return {
        success: false,
        error: error.message || "An error occurred during file processing.",
      };
    }
  }

  async uploadFFmpegChunksWithPreview(
    file,
    onProgress,
    skipTranscription = false,
    duration = 0,
  ) {
    try {
      // Ensure FFmpeg is ready
      await this.ensureFFmpegReady();
      const CHUNK_DURATION_SECONDS = 300; // 5 minutes
      const OVERLAP_SECONDS = 10;
      const chunks = []; // For preview

      // Get file extension
      const ext = file.name.split(".").pop().toLowerCase();
      const filename = "input." + ext;
      
      console.log("Starting FFmpeg chunking with preview for:", filename);
      
      // Report initial progress
      if (onProgress) {
        onProgress(5, "Reading file info...");
      }

      // Write file to FFmpeg filesystem
      this.ffmpeg.FS("writeFile", filename, await fetchFile(file));

      // Get total duration
      let totalDuration = 0;
      const logMessages = [];
      this.ffmpeg.setLogger(({ message }) => {
        logMessages.push(message);
      });

      await this.ffmpeg.run("-i", filename);
      
      const match = logMessages
        .find((msg) => msg.includes("Duration:"))
        ?.match(/Duration: (\d+):(\d+):(\d+\.\d+)/);

      if (match) {
        const [_, h, m, s] = match;
        totalDuration = parseInt(h) * 3600 + parseInt(m) * 60 + parseFloat(s);
        console.log("Total duration:", totalDuration);
      }

      if (totalDuration === 0) {
        throw new Error("Could not determine video/audio duration from file metadata.");
      }

      const numChunks = Math.max(1, Math.ceil(totalDuration / CHUNK_DURATION_SECONDS));
      console.log(`Total number of chunks: ${numChunks}`);

      // Generate unique upload ID for this chunked upload
      const uploadId = `ffmpeg-${file.name}-${Date.now()}`;

      // Process each chunk
      for (let i = 0; i < numChunks; i++) {
        const startTime = Math.max(0, i * CHUNK_DURATION_SECONDS - (i > 0 ? OVERLAP_SECONDS : 0));
        const chunkDuration = i === numChunks - 1 
          ? totalDuration - startTime 
          : CHUNK_DURATION_SECONDS + (i > 0 ? OVERLAP_SECONDS : 0);
        
        const outputFileName = `c-${i}-${file.name}`;
        const tempOutputFileName = `c-${i}.${ext}`;

        // Report splitting progress
        if (onProgress) {
          onProgress(
            10 + (i / numChunks) * 40,
            `Splitting file... (${i + 1}/${numChunks})`
          );
        }

        // Split the file using FFmpeg
        await this.ffmpeg.run(
          "-i",
          filename,
          "-ss",
          startTime.toString(),
          "-t",
          chunkDuration.toString(),
          "-c",
          "copy",
          tempOutputFileName
        );

        // Read the chunk data
        const data = this.ffmpeg.FS("readFile", tempOutputFileName);
        const chunkBlob = new Blob([data.buffer], { type: file.type });

        // Convert blob to base64 for persistent storage (only for reasonably sized chunks)
        const MAX_CHUNK_SIZE_FOR_PREVIEW = 50 * 1024 * 1024; // 50MB limit for preview
        let chunkUrl = null;
        
        if (chunkBlob.size <= MAX_CHUNK_SIZE_FOR_PREVIEW) {
          chunkUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(chunkBlob);
          });
        }

        // Store chunk info for preview with base64 data
        chunks.push({
          index: i,
          name: outputFileName,
          url: chunkUrl, // Will be null for very large chunks
          startTime: startTime,
          duration: chunkDuration,
          size: chunkBlob.size,
          type: chunkBlob.type,
        });

        // Upload the chunk using the existing backend chunk endpoint
        const formData = new FormData();
        formData.append("file", chunkBlob, outputFileName);
        formData.append("uploadId", uploadId);
        formData.append("chunkIndex", i.toString());
        formData.append("totalChunks", numChunks.toString());
        formData.append("fileName", file.name);

        // Report upload progress
        if (onProgress) {
          onProgress(
            50 + ((i + 1) / numChunks) * 45,
            `Uploading chunk... (${i + 1}/${numChunks})`
          );
        }

        try {
          await this.uploadChunk(formData);
        } catch (error) {
          console.error(`Error uploading chunk ${i}:`, error);
          throw new Error(`Failed to upload chunk ${i + 1}.`);
        }

        // Clean up temporary file
        this.ffmpeg.FS("unlink", tempOutputFileName);
      }

      // Clean up input file
      this.ffmpeg.FS("unlink", filename);

      // Report finalizing progress
      if (onProgress) {
        onProgress(98, "Finalizing...");
      }

      // Complete the chunked upload using the existing backend endpoint
      const response = await this.completeChunkedUpload({
        uploadId,
        fileName: file.name,
        totalChunks: numChunks,
        mimetype: file.type,
        skipTranscription,
        duration: totalDuration,
      });

      if (response.data && response.data.googleFileID) {
        if (onProgress) {
          onProgress(100, "Done!");
        }
        return {
          success: true,
          jobId: response.data.googleFileID,
          dbId: response.data.id,
          audioChunks: chunks, // Return chunks for preview
        };
      } else {
        throw new Error("Failed to start transcription job.");
      }

    } catch (error) {
      console.error("Error in FFmpeg chunking with preview:", error);
      return {
        success: false,
        error: error.message || "An error occurred during file processing.",
      };
    }
  }

  async validateToken() {
    const token = localStorage.getItem("authToken");
    if (!token) {
      return { success: false, error: "No token found" };
    }
    try {
      // **หมายเหตุ:** Endpoint นี้คือสิ่งที่คุณต้องสร้างใน Backend (เช่น /auth/validate)
      // มันควรจะรอรับ GET request ที่มี Authorization header
      const response = await this.client.get("/validate-token");

      if (response.data && response.data.success) {
        return { success: true };
      } else {
        throw new Error("Token validation failed on server");
      }
    } catch (error) {
      console.error("Token validation failed:", error);
      // ถ้าเกิด Error (เช่น 401 Unauthorized), แสดงว่า Token ไม่ถูกต้อง
      this.logout(); // <-- **สำคัญ:** ลบ Token ที่ไม่ถูกต้องทิ้ง
      return { success: false, error: "Invalid or expired token" };
    }
  }

  async checkTranscriptionStatus(jobId, dbId) {
    try {
      // NOTE: This webhook path must also be new.
      // Example: /check-transcribe-status?jobId=THE_ID
      console.log("checking checkTranscriptionStatus", jobId, dbId);
      const response = await this.client.get(
        `/upload/check-transcribe-status`,
        {
          params: {
            jobId,
            locadtionURL:
              typeof window !== "undefined" ? window.location.origin : "",
            dbId,
          },
        }
      );
      // EXPECTING n8n to return:
      // 1. { "status": "processing" }
      // 2. { "status": "completed", "transcript": "The final text..." }
      // 3. { "status": "failed", "error": "Something went wrong." }
      return response.data;
    } catch (error) {
      console.error(`Error checking status for job ${jobId}:`, error);
      return { status: "transcribing", error: error.message };
    }
  }

  async restartTranscription(jobId) {
    try {
      // NOTE: This assumes you have an n8n endpoint that can restart a job
      // using an existing file ID associated with the jobId.
      const response = await this.client.post("/restart-transcribe", { jobId });
      if (response.data && response.data.success) {
        return { success: true };
      }
      throw new Error("Failed to restart the job on the server.");
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async startLongTestProcess() {
    try {
      console.log("API SERVICE: Requesting to start a long process...");
      const response = await this.localTestClient.post("/delay/10");
      // The server responds with { jobId: "..." }
      return response.data;
    } catch (error) {
      console.error("API SERVICE: Error starting test process:", error);
      // Re-throw the error so the component can handle it
      throw error;
    }
  }
  async checkTestStatus(jobId) {
    try {
      console.log(`API SERVICE: Checking status for jobId: ${jobId}`);
      const response = await this.localTestClient.get(`/status/${jobId}`);
      // The server responds with { status: "...", result: { ... } }
      return response.data;
    } catch (error) {
      console.error(`API SERVICE: Error checking status for ${jobId}:`, error);
      // Re-throw the error so the component can handle it
      throw error;
    }
  }

  async uploadAttachment(file) {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await this.client.post(
        "/upload/uploadAttachment",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading attachment:", error);
      return { success: false, error: error.message };
    }
  }

  async testDirectTimeoutRequest() {
    try {
      console.log(
        "API SERVICE: Making a direct 5-minute request... (expecting timeout)"
      );
      // We must override the default timeout for this specific request,
      // otherwise Axios will kill it first. We set it > 5 minutes.
      const response = await this.localTestClient.get(
        "/direct-request-timeout",
        {
          timeout: 420000, // 6 minutes timeout on the client side
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        "API SERVICE: The direct request failed as expected.",
        error
      );
      // Re-throw the error so the component can display a message.
      throw error;
    }
  }

  // --- START: MODIFIED LOGIC ---
  // File upload and transcription
  async uploadAndTranscribe(file) {
    const formData = new FormData();
    // **หมายเหตุ:** ตรวจสอบให้แน่ใจว่าใน n8n Webhook ของคุณ
    // ตั้งค่า Binary Property เป็น "file" เพื่อให้ตรงกับ key นี้
    formData.append("file", file);

    try {
      const response = await this.client.post(
        "/upload/uploadAndTranscribe",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Raw response from /uploadAndTranscribe:", response.data);

      // --- ส่วนสำคัญ: แกะข้อมูลจาก response ที่ซับซ้อน ---
      const rawData = response.data;
      // ใช้ optional chaining (?.) เพื่อป้องกัน error หากโครงสร้างข้อมูลไม่ถูกต้อง
      const transcriptText =
        rawData?.[0]?.candidates?.[0]?.content?.parts?.[0]?.text;

      // ตรวจสอบว่าได้ข้อความ transcript กลับมาหรือไม่
      if (transcriptText) {
        // ถ้าสำเร็จ, ส่ง object ที่สะอาดและพร้อมใช้งานกลับไป
        return {
          success: true,
          transcript: transcriptText.trim(), // .trim() เพื่อลบช่องว่างหัว-ท้าย
          fileName: file.name,
          fileSize: file.size,
        };
      } else {
        // กรณีที่ API response กลับมาแต่ไม่มี transcript
        console.error("Could not parse transcript from API response:", rawData);
        // คืนค่า object ที่บ่งบอกถึงความล้มเหลว
        return {
          success: false,
          error: "Transcription text not found in the response.",
        };
      }
    } catch (error) {
      console.error("Error during transcription request:", error);
      // จัดการ error จาก network หรืออื่นๆ
      return {
        success: false,
        error: error.message || "An unknown network error occurred.",
      };
    }
  }
  // --- END: MODIFIED LOGIC ---

  // Chat with transcript context
  async chat(payload) {
    // **หมายเหตุ:** ตรวจสอบให้แน่ใจว่า n8n Webhook ของคุณ
    // รอรับข้อมูลในโครงสร้างนี้ที่ /chat
    const response = await this.client.post("/chat", payload);
    return response.data;
  }

  async getChatStream(payload) {
    const streamUrl = `${API_BASE_URL}/chat/stream`;
    const token = localStorage.getItem("authToken");

    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return fetch(streamUrl, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(payload),
    });
  }

  async verifyPin(pin) {
    try {
      // **หมายเหตุ:** ตรวจสอบว่า n8n Webhook ของคุณมี endpoint
      // ที่รอรับ POST request ที่ /verify-pin และมี body เป็น { "pin": "1234" }
      const response = await this.client.post("/verify-pin", { pin });

      // สมมติว่า API ตอบกลับมาเป็น { success: true } หรือ { success: false }
      return response.data;
    } catch (error) {
      console.error("Error verifying PIN:", error);
      // คืนค่า error ที่ชัดเจน
      return {
        success: false,
        error: "เกิดข้อผิดพลาดในการเชื่อมต่อเพื่อตรวจสอบ PIN",
      };
    }
  }

  async sendEmail(emailPayload) {
    try {
      // **หมายเหตุ:** ตรวจสอบให้แน่ใจว่าใน n8n Webhook ของคุณ
      // มี endpoint ที่รอรับ POST request ที่ /send-email
      const response = await this.client.post("mail/send", emailPayload);

      // คืนค่า response ทั้งหมดเพื่อให้ component ตัดสินใจได้
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error sending email via API:", error);
      // คืนค่า object ที่บ่งบอกถึงความล้มเหลวพร้อมข้อความ error
      return {
        success: false,
        error:
          error.response?.data?.message ||
          error.message ||
          "An unknown error occurred.",
      };
    }
  }

  // Speech-to-text for voice chat
  async speechToText(audioBlob) {
    const formData = new FormData();
    formData.append("audio", audioBlob, "speech.wav");
    const response = await this.client.post("/speech-to-text", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    console.log("response from STT", response.data);
    return response.data;
  }

  // Text-to-speech
  async textToSpeech(text) {
    const response = await this.client.post(
      "/text-to-speech",
      {
        text,
      },
      {
        responseType: "blob",
      }
    );
    return response.data;
  }

  // History management
  async saveTranscript(transcript) {
    const response = await this.client.post("/transcripts", transcript);
    return response.data;
  }

  async getTranscripts() {
    const response = await this.client.get("/transcripts");
    return response.data;
  }

  async deleteTranscript(id) {
    const response = await this.client.delete(`/transcripts/${id}`);
    return response.data;
  }

  async getProfileDetails() {
    const response = await this.client.get("/user/profile-details");
    return response.data;
  }

  async updateUserPrompts(data) {
    const response = await this.client.patch("/user/prompts", data);
    return response.data;
  }

  async updateApiKey(data) {
    const response = await this.client.patch("/user/api-key", data);
    return response.data;
  }

  async checkApiKey(apiKey) {
    try {
      await axios.get(
        `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`
      );
      return { success: true };
    } catch (error) {
      return { success: false, error: "Invalid API Key" };
    }
  }

  async aiAssist(transcript) {
    try {
      const response = await this.client.post("/prompt/ai-assist", {
        transcript,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }

  async retryTranscription(dbId) {
    try {
      const response = await this.client.post("/upload/retry-transcription", {
        dbId,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }

  async retranscribe(dbId) {
    try {
      const response = await this.client.post("/upload/retranscribe", { dbId });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  }

  async generatePodcast(data) {
    // This is now the start endpoint
    const response = await this.client.post("/podcast/generate", data);
    return response.data; // Should return { jobId: "..." }
  }

  async getPodcastStatus(jobId) {
    const response = await this.client.get(`/podcast/status/${jobId}`);
    return response.data;
  }
}

export default new ApiService();
